import React, { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { useFirebase } from '../contexts/FirebaseContext'
import { 
  Users,
  FileText,
  Globe,
  Monitor,
  StickyNote,
  Trophy
} from 'lucide-react'
import SkeletonCard from '../components/SkeletonCard'
import toast from 'react-hot-toast'

const Dashboard = () => {
  const [analytics, setAnalytics] = useState(null)
  const [loading, setLoading] = useState(true)
  const { getAnalytics } = useFirebase()
  const isInitialLoad = useRef(true)

  useEffect(() => {
    loadAnalytics(true)
  }, [])

  const loadAnalytics = async (skipToast = false) => {
    setLoading(true)
    try {
      const data = await getAnalytics()
      setAnalytics(data)
      // Only show success toast on manual refresh, not initial load
      if (!skipToast && !isInitialLoad.current) {
        toast.success('Analytics refreshed successfully')
      }
      isInitialLoad.current = false
    } catch (error) {
      console.error('Error loading analytics:', error)
      toast.error('Failed to load analytics')
    } finally {
      setLoading(false)
    }
  }

  const stats = [
    {
      name: 'Total Users',
      value: analytics?.totalUsers || 0,
      icon: Users,
      color: 'danger',
      href: '/users'
    },
    {
      name: 'Total Notes',
      value: analytics?.totalNotes || 0,
      icon: StickyNote,
      color: 'blue',
      href: '/alerts'
    },
    {
      name: 'Total Alerts',
      value: analytics?.totalUpdates || 0,
      icon: FileText,
      color: 'warning',
      href: '/updates'
    },
    {
      name: 'Total Online Tests',
      value: analytics?.totalOnlineTests || 0,
      icon: Globe,
      color: 'success',
      href: '/home?tab=online'
    },
    {
      name: 'Total Offline Tests',
      value: analytics?.totalOfflineTests || 0,
      icon: Monitor,
      color: 'blue',
      href: '/home'
    },
    {
      name: 'Leaderboard',
      value: 'Top Users',
      icon: Trophy,
      color: 'warning',
      href: '/leaderboard'
    }
  ]


  // Get current time greeting
  const getTimeGreeting = () => {
    const hour = new Date().getHours()
    if (hour < 12) return 'Good Morning'
    if (hour < 17) return 'Good Afternoon'
    return 'Good Evening'
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header Section */}
      <div className="bg-gradient-to-r from-purple-600 via-purple-700 to-indigo-700 rounded-2xl shadow-2xl p-8 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-white/10 to-transparent"></div>
        <div className="relative z-10">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-4xl font-bold mb-2 animate-slide-in">{getTimeGreeting()}</h1>
              <p className="text-purple-100 text-lg animate-fade-in">
                Welcome back! Here's what's happening today.
              </p>
            </div>
            <button 
              onClick={() => loadAnalytics(false)}
              disabled={loading}
              className="bg-white/20 hover:bg-white/30 text-white px-4 py-2 rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50"
            >
              <svg className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Refresh
            </button>
          </div>
          
          {/* Dashboard Overview Title */}
          <div className="flex items-center gap-3">
            <div className="w-6 h-6 bg-gradient-to-r from-red-400 to-yellow-400 rounded"></div>
            <h2 className="text-2xl font-bold">Dashboard Overview</h2>
          </div>
        </div>
        <div className="absolute -right-10 -top-10 w-32 h-32 bg-white/10 rounded-full blur-xl"></div>
        <div className="absolute -left-10 -bottom-10 w-24 h-24 bg-white/5 rounded-full blur-lg"></div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {loading ? (
          // Show skeleton cards while loading
          Array.from({ length: 5 }).map((_, index) => (
            <SkeletonCard key={index} />
          ))
        ) : (
          stats.map((stat) => {
          const Icon = stat.icon
          const colorClasses = {
            primary: 'bg-primary-500 text-white',
            success: 'bg-success-500 text-white',
            warning: 'bg-warning-500 text-white',
            danger: 'bg-danger-500 text-white',
            blue: 'bg-blue-500 text-white'
          }
          
          return (
            <Link
              key={stat.name}
              to={stat.href}
              className="group"
            >
              <div className="card hover-lift animate-bounce-in p-6 relative overflow-hidden">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <dt className="text-sm font-medium text-gray-600 mb-2 group-hover:text-primary-600 transition-colors">
                      {stat.name}
                    </dt>
                    <dd className="text-3xl font-bold text-gray-900 mb-1">
                      {stat.value.toLocaleString()}
                    </dd>
                  </div>
                  <div className="flex-shrink-0">
                    <div className={`w-16 h-16 rounded-xl flex items-center justify-center ${colorClasses[stat.color]} group-hover:scale-110 transition-all duration-300 shadow-lg`}>
                      <Icon className="h-8 w-8" />
                    </div>
                  </div>
                </div>
                <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-primary-500 to-primary-600 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300"></div>
              </div>
            </Link>
          )
        })
        )}
      </div>
    </div>
  )
}

export default Dashboard
